import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { trainerAssociationService } from '../../services/trainerAssociationService';
import { TrainerCard } from './TrainerCard';
import { TrainerPricingModal } from './TrainerPricingModal';
import { TrainerPermissionsModal } from './TrainerPermissionsModal';
import { ConfirmationModal } from '../common/ConfirmationModal';
import type { TrainerAssociation } from '../../types/trainerAssociation';

interface TrainersSectionProps {
  businessProfileId?: string;
  isCollapsed?: boolean;
  onToggle?: () => void;
}

export function TrainersSection({
  businessProfileId,
  isCollapsed = false,
  onToggle,
}: TrainersSectionProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [associations, setAssociations] = useState<TrainerAssociation[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modal states
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedAssociation, setSelectedAssociation] = useState<TrainerAssociation | null>(null);
  const [removingAssociation, setRemovingAssociation] = useState<TrainerAssociation | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadAssociations = useCallback(async () => {
    if (!token || !businessProfileId) return;

    setIsLoading(true);
    try {
      const data = await trainerAssociationService.getBusinessTrainerAssociations(
        businessProfileId,
        token
      );
      setAssociations(Array.isArray(data) ? data : []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load trainer associations';
      showToast('error', t('common.error'), message);
      setAssociations([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, businessProfileId, showToast, t]);

  // Load data on mount to show count in header
  useEffect(() => {
    if (businessProfileId) {
      loadAssociations();
    }
  }, [businessProfileId, loadAssociations]);

  const pendingAssociations = associations.filter(a => a.status === 'Pending');
  const confirmedAssociations = associations.filter(a =>
    a.status === 'Confirmed' || a.status === 'Accepted'
  );

  const handleEditPricing = (association: TrainerAssociation) => {
    setSelectedAssociation(association);
    setShowPricingModal(true);
  };

  const handleEditPermissions = (association: TrainerAssociation) => {
    setSelectedAssociation(association);
    setShowPermissionsModal(true);
  };

  const handleRemoveTrainer = (association: TrainerAssociation) => {
    setRemovingAssociation(association);
  };

  const handlePricingSave = async (data: { hourlyRate: number; vatRate: number; grossHourlyRate: number }) => {
    if (!token || !businessProfileId || !selectedAssociation) return;

    setIsSaving(true);
    try {
      await trainerAssociationService.updateTrainerPricing(
        businessProfileId,
        selectedAssociation.id,
        data,
        token
      );
      showToast('success', t('trainers.pricingUpdated', 'Cennik zaktualizowany'));
      setShowPricingModal(false);
      setSelectedAssociation(null);
      await loadAssociations();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update pricing';
      showToast('error', t('common.error'), message);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePermissionsSave = async (data: { canRunOwnTrainings: boolean; isEmployee: boolean; maxNumberOfUsers: number | null }) => {
    if (!token || !businessProfileId || !selectedAssociation) return;

    setIsSaving(true);
    try {
      await trainerAssociationService.updateTrainerPermissions(
        businessProfileId,
        selectedAssociation.id,
        data,
        token
      );
      showToast('success', t('trainers.permissionsUpdated', 'Uprawnienia zaktualizowane'));
      setShowPermissionsModal(false);
      setSelectedAssociation(null);
      await loadAssociations();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update permissions';
      showToast('error', t('common.error'), message);
    } finally {
      setIsSaving(false);
    }
  };

  const confirmRemoveTrainer = async () => {
    if (!token || !businessProfileId || !removingAssociation) return;

    setIsSaving(true);
    try {
      await trainerAssociationService.removeTrainerAssociation(
        businessProfileId,
        removingAssociation.id,
        token
      );
      showToast('success', t('trainers.trainerRemoved', 'Trener został usunięty'));
      setAssociations(prev => prev.filter(a => a.id !== removingAssociation.id));
      setRemovingAssociation(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove trainer';
      showToast('error', t('common.error'), message);
    } finally {
      setIsSaving(false);
    }
  };

  const totalCount = confirmedAssociations.length;

  return (
    <div className="bg-brand-primary-deep rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/10 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-brand-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="text-lg font-medium text-white">
            {t('trainers.title', 'Powiązani Trenerzy')}
          </span>
          <span className="text-sm text-white/70">({totalCount})</span>
          {pendingAssociations.length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-brand-accent/20 text-brand-accent text-xs">
              +{pendingAssociations.length} {t('trainers.pending', 'oczekujących')}
            </span>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-white/70 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Section Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4 space-y-4">
          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="bg-white/10 rounded-lg p-4 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-full" />
                    <div className="flex-1">
                      <div className="h-4 bg-white/20 rounded w-32 mb-2" />
                      <div className="h-3 bg-white/20 rounded w-48" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && associations.length === 0 && (
            <div className="text-center py-8 text-white/60">
              <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p>{t('trainers.noTrainers', 'Brak powiązanych trenerów')}</p>
              <p className="text-sm mt-1">
                {t('trainers.trainersWillAppear', 'Trenerzy pojawią się tutaj po zaakceptowaniu ich próśb o współpracę')}
              </p>
            </div>
          )}

          {/* Pending Requests */}
          {!isLoading && pendingAssociations.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-brand-accent flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('trainers.pendingRequests', 'Oczekujące prośby')} ({pendingAssociations.length})
              </h4>
              {pendingAssociations.map(association => (
                <TrainerCard
                  key={association.id}
                  association={association}
                  onEditPricing={() => {}}
                  onEditPermissions={() => {}}
                  onRemove={() => handleRemoveTrainer(association)}
                />
              ))}
            </div>
          )}

          {/* Confirmed Trainers */}
          {!isLoading && confirmedAssociations.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-white/70">
                {t('trainers.confirmedTrainers', 'Potwierdzeni trenerzy')} ({confirmedAssociations.length})
              </h4>
              {confirmedAssociations.map(association => (
                <TrainerCard
                  key={association.id}
                  association={association}
                  onEditPricing={() => handleEditPricing(association)}
                  onEditPermissions={() => handleEditPermissions(association)}
                  onRemove={() => handleRemoveTrainer(association)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pricing Modal */}
      {showPricingModal && selectedAssociation && (
        <TrainerPricingModal
          trainerName={selectedAssociation.trainerDisplayName}
          initialHourlyRate={selectedAssociation.hourlyRate}
          initialVatRate={selectedAssociation.vatRate}
          initialGrossHourlyRate={selectedAssociation.grossHourlyRate}
          onSave={handlePricingSave}
          onClose={() => {
            setShowPricingModal(false);
            setSelectedAssociation(null);
          }}
          isLoading={isSaving}
        />
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && selectedAssociation && (
        <TrainerPermissionsModal
          trainerName={selectedAssociation.trainerDisplayName}
          initialCanRunOwnTrainings={selectedAssociation.canRunOwnTrainings || false}
          initialIsEmployee={selectedAssociation.isEmployee || false}
          initialMaxNumberOfUsers={selectedAssociation.maxNumberOfUsers}
          onSave={handlePermissionsSave}
          onClose={() => {
            setShowPermissionsModal(false);
            setSelectedAssociation(null);
          }}
          isLoading={isSaving}
        />
      )}

      {/* Remove Confirmation */}
      {removingAssociation && (
        <ConfirmationModal
          title={t('trainers.removeConfirmTitle', 'Usuń trenera')}
          message={t('trainers.removeConfirmMessage', {
            name: removingAssociation.trainerDisplayName,
            defaultValue: `Czy na pewno chcesz usunąć ${removingAssociation.trainerDisplayName} z listy trenerów?`,
          })}
          confirmLabel={t('common.remove', 'Usuń')}
          cancelLabel={t('common.cancel', 'Anuluj')}
          onConfirm={confirmRemoveTrainer}
          onCancel={() => setRemovingAssociation(null)}
          isLoading={isSaving}
          variant="danger"
        />
      )}
    </div>
  );
}
